import type { AdminUser, BoardSnapshot, BoardSummary, FileMeta, Update, User, WorkspaceUser } from "./types";

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers ?? {}) },
    credentials: "same-origin",
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error((body as any).error || "request_failed"), { status: res.status, body });
  return body as T;
}

const post = <T>(p: string, data?: unknown) => req<T>(p, { method: "POST", body: JSON.stringify(data ?? {}) });
const put = <T>(p: string, data?: unknown) => req<T>(p, { method: "PUT", body: JSON.stringify(data ?? {}) });
const patch = <T>(p: string, data?: unknown) => req<T>(p, { method: "PATCH", body: JSON.stringify(data ?? {}) });
const del = <T>(p: string) => req<T>(p, { method: "DELETE" });

export const api = {
  // auth
  me: () => req<{ user: User; needs2faSetup?: boolean }>("/auth/me"),
  register: (d: { email: string; password: string; name: string }) => post<{ status: string; approval: string }>("/auth/register", d),
  login: (d: { email: string; password: string }) => post<{ status: string }>("/auth/login", d),
  challenge2fa: (code: string) => post<{ status: string; user: User }>("/auth/2fa/challenge", { code }),
  logout: () => post("/auth/logout"),
  setup2fa: () => post<{ secret: string; uri: string }>("/auth/2fa/setup"),
  enable2fa: (code: string) => post<{ enabled: boolean; recoveryCodes: string[]; activated?: boolean }>("/auth/2fa/enable", { code }),
  disable2fa: (password: string) => post<{ enabled: boolean }>("/auth/2fa/disable", { password }),
  changePassword: (currentPassword: string, newPassword: string) => post("/auth/change-password", { currentPassword, newPassword }),
  updateProfile: (name: string) => patch("/auth/me", { name }),

  // boards
  boards: () => req<{ boards: BoardSummary[] }>("/boards"),
  board: (id: string) => req<BoardSnapshot>(`/boards/${id}`),
  createBoard: (name: string) => post<{ board: { id: string; name: string } }>("/boards", { name }),
  deleteBoard: (id: string) => del(`/boards/${id}`),
  renameBoard: (id: string, name: string) => patch(`/boards/${id}`, { name }),
  addItem: (boardId: string, d: { name: string; groupId: string }) => post<{ item: any }>(`/boards/${boardId}/items`, d),
  updateItem: (id: string, d: object) => patch(`/items/${id}`, d),
  deleteItem: (id: string) => del(`/items/${id}`),
  setCell: (itemId: string, colId: string, value: unknown) => put(`/items/${itemId}/cells/${colId}`, { value }),
  addGroup: (boardId: string, d: { title: string; color?: string }) => post<{ group: any }>(`/boards/${boardId}/groups`, d),
  updateGroup: (id: string, d: object) => patch(`/groups/${id}`, d),
  deleteGroup: (id: string) => del(`/groups/${id}`),
  addColumn: (boardId: string, d: { title: string; type: string; settings?: object }) => post<{ column: any }>(`/boards/${boardId}/columns`, d),
  deleteColumn: (id: string) => del(`/columns/${id}`),

  // monday import
  mondayStatus: () => req<{ connected: boolean }>("/integrations/monday"),
  saveMondayToken: (token: string) => post<{ ok: boolean; account?: { name: string } }>("/integrations/monday/token", { token }),
  mondayBoards: () => req<{ boards: Array<{ id: string; name: string; items_count: number }> }>("/integrations/monday/boards"),
  importMonday: (mondayBoardId: string) => post<{ ok: boolean; boardId: string; name: string; imported: number }>("/import/monday", { mondayBoardId }),

  // workspace users (people pickers)
  users: () => req<{ users: WorkspaceUser[] }>("/users"),

  // columns
  updateColumn: (id: string, d: { width?: number; position?: number; hidden?: boolean; title?: string; adminOnly?: boolean }) => patch(`/columns/${id}`, d),

  // admin
  adminUsers: () => req<{ users: AdminUser[] }>("/admin/users"),
  approveUser: (id: string) => post<{ ok: boolean }>(`/admin/users/${id}/approve`),
  rejectUser: (id: string) => post<{ ok: boolean }>(`/admin/users/${id}/reject`),
  setUserRole: (id: string, role: "member" | "producer" | "admin") => post(`/admin/users/${id}/role`, { role }),
  deleteUser: (id: string) => del(`/admin/users/${id}`),

  // updates (item chat)
  updates: (itemId: string) => req<{ updates: Update[]; count: number }>(`/items/${itemId}/updates`),
  postUpdate: (itemId: string, body: string, parentId?: string) => post<{ update: Update }>(`/items/${itemId}/updates`, { body, parentId }),
  deleteUpdate: (id: string) => del(`/updates/${id}`),
  updateCounts: (boardId: string) => req<{ counts: Record<string, number> }>(`/boards/${boardId}/update-counts`),

  // files
  uploadFiles: async (itemId: string, colId: string, fileList: File[]) => {
    const fd = new FormData();
    for (const f of fileList) fd.append("file", f);
    const res = await fetch(`/api/items/${itemId}/cells/${colId}/files`, { method: "POST", body: fd, credentials: "same-origin" });
    const b = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error((b as any).error || "upload_failed"), { body: b });
    return b as { ok: boolean; files: FileMeta[] };
  },
  deleteFile: (id: string) => del(`/files/${id}`),
  fileUrl: (id: string, download = false) => `/api/files/${id}${download ? "?download=1" : ""}`,
};
